/* ==========================================================================
   WAKERS — motion layer
   Uses Motion One (window.Motion, from motion.min.js) for scroll-triggered
   reveals and anime.js (window.anime) for playful hover micro-interactions.

   Safety model:
   - Does nothing if the user prefers reduced motion.
   - Only hides elements that are currently BELOW the fold (off-screen), so a
     reveal never causes a visible flash, and above-the-fold content is never
     hidden.
   - A failsafe reveals everything after a timeout in case anything stalls.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  var EASE_OUT = [0.22, 1, 0.36, 1];
  var EASE_POP = [0.34, 1.56, 0.64, 1];

  // Wait for the libraries (loaded right before this script). Poll briefly.
  function ready(cb) {
    var tries = 0;
    (function check() {
      if (window.Motion && window.anime) return cb();
      if (tries++ > 60) return cb(); // ~3s; proceed with whatever loaded
      setTimeout(check, 50);
    })();
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.top < (window.innerHeight || document.documentElement.clientHeight) && r.bottom > 0;
  }

  // Reveal a set of elements. Elements already on screen animate immediately
  // (intentional intro); off-screen ones are hidden and revealed on scroll-in.
  function reveal(els, opts) {
    opts = opts || {};
    var Motion = window.Motion;
    var stagger = opts.stagger != null ? opts.stagger : 0.06;
    var y = opts.y != null ? opts.y : 26;
    var duration = opts.duration != null ? opts.duration : 0.6;

    if (!Motion || !Motion.animate) {
      // No library — leave everything visible.
      for (var i = 0; i < els.length; i++) els[i].classList.remove('wk-reveal');
      return;
    }

    els.forEach(function (el, i) {
      el.classList.add('wk-reveal');
      var delay = Math.min(i * stagger, 0.6);
      var animateIn = function () {
        Motion.animate(
          el,
          { opacity: [0, 1], y: [y, 0] },
          { duration: duration, delay: delay, ease: EASE_OUT, easing: EASE_OUT }
        );
      };

      if (inViewport(el)) {
        // Set hidden then animate in on the next frame — this is the hero /
        // first-screen intro, so the brief transition reads as intentional.
        el.style.opacity = '0';
        el.style.transform = 'translateY(' + y + 'px)';
        requestAnimationFrame(animateIn);
      } else {
        el.style.opacity = '0';
        el.style.transform = 'translateY(' + y + 'px)';
        if (Motion.inView) {
          Motion.inView(
            el,
            function () {
              animateIn();
            },
            { margin: '0px 0px -12% 0px' }
          );
        } else {
          animateIn();
        }
      }
    });
  }

  // Pick the elements worth staggering inside a section.
  function itemsFor(section) {
    var sel = [
      '.product-grid__item',
      '.resource-list__item',
      '.collection-list__item'
    ];
    for (var i = 0; i < sel.length; i++) {
      var found = section.querySelectorAll(sel[i]);
      if (found.length > 1) return Array.prototype.slice.call(found);
    }
    // Feature / icon-with-text cards live as top-level group blocks.
    var groups = section.querySelectorAll('.section-content-wrapper > .group-block');
    if (groups.length > 1) return Array.prototype.slice.call(groups);
    return null;
  }

  function initReveals() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var sections = Array.prototype.slice.call(main.querySelectorAll(':scope > .shopify-section'));

    sections.forEach(function (section, idx) {
      // The hero has its own dedicated WOW routine (initHero) — skip it here.
      if (section.querySelector('.hero')) return;
      var items = itemsFor(section);

      if (items) {
        reveal(items, { stagger: 0.07, y: 30, duration: 0.55 });
      } else {
        // Reveal the section's main content wrapper as a single unit.
        var wrap = section.querySelector('.section-content-wrapper, .section, .rte, .text-block');
        if (wrap) reveal([wrap], { stagger: 0, y: 28, duration: 0.65 });
      }
    });
  }

  // Playful hover: cards lift + bump, icons give a quick wiggle.
  function initHovers() {
    var anime = window.anime;
    if (!anime) return;

    var cards = document.querySelectorAll(
      '#MainContent .section-content-wrapper > .group-block, #MainContent .product-grid__item .card, .wakers-feature'
    );
    cards.forEach(function (card) {
      var icon = card.querySelector('svg, .icon, img');
      card.addEventListener('mouseenter', function () {
        anime.remove(card);
        anime({ targets: card, scale: 1.03, duration: 320, easing: 'easeOutElastic(1, .6)' });
        if (icon) {
          anime.remove(icon);
          anime({ targets: icon, rotate: ['0deg', '-6deg', '6deg', '0deg'], duration: 500, easing: 'easeInOutSine' });
        }
      });
      card.addEventListener('mouseleave', function () {
        anime.remove(card);
        anime({ targets: card, scale: 1, duration: 300, easing: 'easeOutQuad' });
      });
    });

    // Cute press feedback on primary buttons / add-to-cart.
    var buttons = document.querySelectorAll('.button:not(.button-unstyled), button.button');
    buttons.forEach(function (btn) {
      btn.classList.add('wk-pop-target');
      btn.addEventListener('mousedown', function () {
        anime.remove(btn);
        anime({ targets: btn, scale: 0.94, duration: 120, easing: 'easeOutQuad' });
      });
      var back = function () {
        anime.remove(btn);
        anime({ targets: btn, scale: 1, duration: 260, easing: 'easeOutElastic(1, .5)' });
      };
      btn.addEventListener('mouseup', back);
      btn.addEventListener('mouseleave', back);
    });
  }

  // The hero: letter-by-letter headline, popping mascots, drifting sparkles,
  // and mouse + scroll parallax across depth layers.
  function initHero() {
    var Motion = window.Motion, anime = window.anime;
    var hero = document.querySelector('.hero');
    if (!hero || hero.__wkHero) return;
    hero.__wkHero = true;

    var content = hero.querySelector('.section-content-wrapper');
    var media = hero.querySelector('.hero__media-wrapper');
    var title = hero.querySelector('.text-block.h1') || hero.querySelector('.h1');
    var subtitle = hero.querySelector('.text-block.rte');
    var button = hero.querySelector('.button');
    var backpack = hero.querySelector('.backpack-svg');
    var dino = hero.querySelector('.dino-svg');

    // 1) Split the headline into letters and pop them in.
    var titleP = title ? (title.querySelector('p') || title) : null;
    if (titleP && anime) {
      var text = titleP.textContent;
      titleP.setAttribute('aria-label', text.trim());
      var html = '';
      for (var i = 0; i < text.length; i++) {
        var ch = text[i];
        html += ch === ' '
          ? '<span class="wk-space"></span>'
          : '<span class="wk-letter" aria-hidden="true">' + ch + '</span>';
      }
      titleP.innerHTML = html;
      var letters = titleP.querySelectorAll('.wk-letter');
      letters.forEach(function (l) { l.style.opacity = '0'; });
      anime({
        targets: letters,
        translateY: [55, 0],
        opacity: [0, 1],
        rotate: [-14, 0],
        scale: [0.4, 1],
        duration: 1100,
        delay: anime.stagger(48, { start: 250 }),
        easing: 'easeOutElastic(1, .6)'
      });
    }

    // 2) Mascot SVGs pop with a spin.
    if (anime) {
      [backpack, dino].forEach(function (el, i) {
        if (!el) return;
        el.style.opacity = '0';
        anime({
          targets: el,
          opacity: [0, 1],
          scale: [0, 1],
          rotate: i === 0 ? [-40, 0] : [40, 0],
          duration: 1200,
          delay: 150 + i * 160,
          easing: 'easeOutElastic(1, .5)'
        });
      });
      // 3) Copy + button drift up.
      [subtitle, button].forEach(function (el, i) {
        if (!el) return;
        el.style.opacity = '0';
        anime({ targets: el, translateY: [26, 0], opacity: [0, 1], duration: 800, delay: 760 + i * 150, easing: 'easeOutQuad' });
      });
    }

    // 4) Drifting sparkle layer over the media.
    if (anime && (media || hero)) {
      var fx = document.createElement('div');
      fx.className = 'wk-hero-fx';
      (media || hero).appendChild(fx);
      var SPARK = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c1 6 4 9 12 12-8 3-11 6-12 12-1-6-4-9-12-12C8 9 11 6 12 0Z"/></svg>';
      var palette = ['#fed94a', '#a9d49f', '#cebddd', '#f7c4af', '#ffffff'];
      var N = window.innerWidth < 750 ? 9 : 16;
      for (var s = 0; s < N; s++) {
        var sp = document.createElement('span');
        sp.className = 'wk-hero-spark';
        var size = 6 + Math.round(Math.random() * 16);
        sp.style.width = size + 'px';
        sp.style.height = size + 'px';
        sp.style.left = (Math.random() * 100).toFixed(2) + '%';
        sp.style.top = (Math.random() * 100).toFixed(2) + '%';
        sp.style.color = palette[s % palette.length];
        sp.innerHTML = SPARK;
        fx.appendChild(sp);
        anime({ targets: sp, opacity: [0, 0.85], scale: [0, 1], duration: 800, delay: 400 + s * 55, easing: 'easeOutBack' });
        anime({
          targets: sp,
          translateY: [0, -(14 + Math.random() * 18)],
          rotate: [0, s % 2 ? 40 : -40],
          duration: 2600 + Math.random() * 1900,
          delay: 1000 + s * 55,
          loop: true,
          direction: 'alternate',
          easing: 'easeInOutSine'
        });
      }
    }

    // 5) Mouse + scroll parallax across depth layers.
    var layers = [];
    if (media) layers.push({ el: media, mx: -18, my: -13, sc: 0.10, scale: 1.06 });
    if (content) layers.push({ el: content, mx: 13, my: 10, sc: -0.04, scale: 1 });
    if (backpack) layers.push({ el: backpack, mx: 30, my: 22, sc: 0.06, scale: 1 });
    if (dino) layers.push({ el: dino, mx: -26, my: 20, sc: 0.08, scale: 1 });

    if (layers.length) {
      var tmx = 0, tmy = 0, scrollY = window.pageYOffset || 0;
      var active = false; // start after intro so we don't fight the pop transforms

      window.addEventListener('mousemove', function (e) {
        tmx = (e.clientX / window.innerWidth - 0.5) * 2;
        tmy = (e.clientY / window.innerHeight - 0.5) * 2;
      }, { passive: true });
      window.addEventListener('scroll', function () { scrollY = window.pageYOffset || 0; }, { passive: true });

      layers.forEach(function (l) { l.cx = 0; l.cy = 0; });
      function frame() {
        var heroH = hero.offsetHeight || 1;
        var sp = Math.max(0, Math.min(scrollY, heroH));
        for (var i = 0; i < layers.length; i++) {
          var l = layers[i];
          var tx = active ? tmx * l.mx : 0;
          var ty = (active ? tmy * l.my : 0) + sp * l.sc;
          l.cx += (tx - l.cx) * 0.08;
          l.cy += (ty - l.cy) * 0.08;
          l.el.style.transform = 'translate3d(' + l.cx.toFixed(2) + 'px,' + l.cy.toFixed(2) + 'px,0)' + (l.scale !== 1 ? ' scale(' + l.scale + ')' : '');
        }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
      setTimeout(function () { active = true; }, 1600);
    }

    // Failsafe: never leave any hero element hidden.
    setTimeout(function () {
      hero.querySelectorAll('.wk-letter, .backpack-svg, .dino-svg, .text-block, .button, .wk-hero-spark').forEach(function (el) {
        if (getComputedStyle(el).opacity === '0') el.style.opacity = '1';
      });
    }, 4500);
  }

  function start() {
    try {
      initHero();
      initReveals();
      initHovers();
    } catch (e) {
      // On any error, make sure nothing stays hidden.
      document.documentElement.classList.add('wk-anim-done');
    }
    // Belt-and-braces: reveal everything after a few seconds no matter what.
    setTimeout(function () {
      document.documentElement.classList.add('wk-anim-done');
    }, 4000);
  }

  function boot() {
    ready(start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Re-run reveals/hovers for sections swapped in by the theme editor.
  document.addEventListener('shopify:section:load', function () {
    ready(function () {
      try {
        initHero();
        initReveals();
        initHovers();
      } catch (e) {}
    });
  });
})();
